/**
 * Shared chrome for the unauthenticated public pages (About, Privacy, Terms).
 *
 * These pages exist so they can be linked from the Google API OAuth consent
 * screen / API access application, so they must render without any auth,
 * organization context, or network calls.
 */

import React from 'react';
import { Link } from 'react-router-dom';

export const COMPANY_NAME = 'XYZ Strategy';
export const PRODUCT_NAME = 'AdCanvas';
export const CONTACT_EMAIL = 'ken@xyzstrategy.co';
export const SITE_URL = 'https://adcanvas.xyzstrategy.co';
export const LAST_UPDATED = 'August 22, 2026';

interface PublicPageLayoutProps {
  /** Page title. Omit when the page supplies its own hero. */
  title?: string;
  subtitle?: string;
  showLastUpdated?: boolean;
  /** 'prose' (default) constrains the body for reading; 'full' lets the page manage its own widths. */
  width?: 'prose' | 'full';
  children: React.ReactNode;
}

export function PublicPageLayout({
  title,
  subtitle,
  showLastUpdated,
  width = 'prose',
  children,
}: PublicPageLayoutProps) {
  const isProse = width === 'prose';
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link to="/about" className="text-lg font-semibold">
            {PRODUCT_NAME}
          </Link>
          <nav className="flex gap-4 text-sm text-muted-foreground">
            <Link to="/about" className="hover:text-foreground">About</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
          </nav>
        </div>
      </header>

      <main className={isProse ? 'mx-auto max-w-3xl px-6 py-12' : ''}>
        {title && (
          <>
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
            {subtitle && <p className="mt-3 text-lg text-muted-foreground">{subtitle}</p>}
            {showLastUpdated && (
              <p className="mt-2 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            )}
          </>
        )}
        <div className={isProse ? 'mt-8 space-y-8 leading-relaxed' : ''}>{children}</div>
      </main>

      <footer className="border-t">
        <div className="mx-auto flex max-w-3xl flex-col gap-2 px-6 py-8 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <span>© {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.</span>
          <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-foreground">{CONTACT_EMAIL}</a>
        </div>
      </footer>
    </div>
  );
}
